<script lang="ts">
// Exposed for useSelect
export const TEXT_INPUT_WRAPPER_CLASS = "sticky-note-text-editable";
</script>

<script setup lang="ts">
import { computed, nextTick, ref, toRefs, useTemplateRef } from "vue";

import { toggleActions } from "@/action/toggleActions";
import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import StickyNoteTextDisplay from "@/components/sticky/StickyNoteTextDisplay.vue";
import StickyNoteTextWrapper from "@/components/sticky/StickyNoteTextWrapper.vue";
import { useBoardStore } from "@/store/board";
import { usePanModeStore } from "@/store/panMode";
import { useZoomStore } from "@/store/zoom";
import { injectStrict } from "@/utils/vue";

import StickyNoteTextEditor from "./internal/StickyNoteTextEditor.vue";

defineProps<{ notOnBoard?: boolean }>();

defineExpose({ focus: focusAndStartEditing });

const emit = defineEmits(["deactivate-note"]);

const card = injectStrict(cardKey);
const cardMeta = injectStrict(cardMetaKey);
const { isReadonly } = toRefs(cardMeta);

const containerRef = useTemplateRef("container");

interface TextComponent {
  focus?: () => void;
  element: () => HTMLElement | null;
}
const editorRef = useTemplateRef<TextComponent>("editor");
const textDisplayRef = useTemplateRef<TextComponent>("textDisplay");

const isEditing = ref(false);

const textAsButton = computed(() => !isReadonly.value && useBoardStore().activeCardId === card.id);

const activeRef = computed(() => editorRef.value ?? textDisplayRef.value);
const renderedEl = computed(() => activeRef.value?.element());

async function focusAndStartEditing() {
  if (usePanModeStore().active) toggleActions.hidePanMode("internal");
  if (isReadonly.value) return;

  isEditing.value = true;
  await nextTick();
  activeRef.value?.focus?.();
}

async function handleBlur() {
  // zooming removes the StickyNote from the DOM and causes a blur event, ignore it
  if (useZoomStore().zooming) return;

  isEditing.value = false;
  await nextTick();
  containerRef.value?.recalculateFontSize(card.text);
}
</script>

<template>
  <StickyNoteTextWrapper
    ref="container"
    :class="TEXT_INPUT_WRAPPER_CLASS"
    :not-on-board
    :rendered-el
    :is-editing
  >
    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
    <template #default="{ fontSize, lines }">
      <StickyNoteTextEditor
        v-if="isEditing"
        ref="editor"
        :font-size
        @deactivate-note="emit('deactivate-note')"
        @blur="handleBlur"
      />
      <StickyNoteTextDisplay
        v-if="!isEditing"
        ref="textDisplay"
        :font-size
        :lines
        :role="textAsButton ? 'button' : 'presentation'"
        :tabindex="textAsButton ? 0 : undefined"
        @keydown.enter.space.stop.prevent="focusAndStartEditing"
      />
    </template>
  </StickyNoteTextWrapper>
</template>
